// src/components/ui/forms/CommunicationsTemplateDetailsForm/CommunicationsTemplateDetailsFormShadcn.jsx
import React, { useState, useEffect } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
  Save,
  Trash2,
  Lock,
  Check,
  X
} from 'lucide-react';

// shadcn components
import { Button } from '../../button';
import { Input } from '../../input';
import { Label } from '../../label';
import { Textarea } from '../../textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../card';

import { cn } from '../../../../utils/cn';
import { getAvailableTriggers, getTriggerLabel } from '../../../../utils/templateDefaults';

// Enhanced Variable categories for the custom button - WITH INVOICE VARIABLES
const VARIABLE_CATEGORIES = [
  {
    id: 'customer',
    label: '👤 Customer Info',
    variables: [
      { key: 'customer_name', label: 'Customer Name' },
      { key: 'customer_email', label: 'Customer Email' },
      { key: 'customer_phone', label: 'Customer Phone' }
    ]
  },
  {
    id: 'appointment',
    label: '📅 Appointment Details',
    variables: [
      { key: 'event_date', label: 'Event Date' },
      { key: 'event_time', label: 'Event Time' },
      { key: 'appointment_date', label: 'Appointment Date' },
      { key: 'appointment_time', label: 'Appointment Time' },
      { key: 'service_address', label: 'Service Address' },
      { key: 'service_type', label: 'Service Type' }
    ]
  },
  {
    id: 'business',
    label: '🏢 Business Info',
    variables: [
      { key: 'business_name', label: 'Business Name' },
      { key: 'business_phone', label: 'Business Phone' },
      { key: 'business_email', label: 'Business Email' },
      { key: 'business_initials', label: 'Business Initials' }
    ]
  },
  {
    id: 'invoice',
    label: '💰 Invoice Details',
    emailOnly: true,
    variables: [
      { key: 'invoice_number', label: 'Invoice Number' },
      { key: 'invoice_date', label: 'Invoice Date' },
      { key: 'due_date', label: 'Due Date' },
      { key: 'total_amount', label: 'Total Amount' },
      { key: 'subtotal', label: 'Subtotal' },
      { key: 'tax_amount', label: 'Tax Amount' },
      { key: 'line_items_summary', label: 'Line Items Summary' },
      { key: 'payment_url', label: 'Payment URL' },
      { key: 'notes', label: 'Invoice Notes' }
    ]
  }
];

// Convert HTML to plain text
const htmlToText = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Replace <br> tags with line breaks
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/<br\s*\/?>/gi, '\n');

  // Replace </p> tags with double line breaks
  tempDiv.innerHTML = tempDiv.innerHTML.replace(/<\/p>/gi, '\n\n');

  // Get text content
  let text = tempDiv.textContent || tempDiv.innerText || '';

  // Clean up extra whitespace
  text = text.replace(/\n\s*\n\s*\n/g, '\n\n'); // Remove triple+ line breaks
  text = text.trim();

  return text;
};

// Get variable label from key
const getVariableLabelFromKey = (variableKey) => {
  for (const category of VARIABLE_CATEGORIES) {
    const variable = category.variables.find(v => v.key === variableKey);
    if (variable) return variable.label;
  }
  return variableKey; // Fallback to key if not found
};

// Convert {{brackets}} to pills when loading into editor
const convertBracketsToPills = (html) => {
  if (!html) return html;
  return html.replace(/\{\{([^}]+)\}\}/g, (match, variableKey) => {
    const variableLabel = getVariableLabelFromKey(variableKey.trim());
    return `<span class="variable-pill" data-variable="${variableKey.trim()}" contenteditable="false">${variableLabel}</span>`;
  });
};

// Convert pills back to {{brackets}} when saving
const convertPillsToBrackets = (html) => {
  if (!html) return html;
  // Updated regex to handle attributes in any order
  return html.replace(/<span[^>]*class="variable-pill"[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '{{$1}}');
};

const CommunicationsTemplateDetailsFormShadcn = ({
  template,
  templateType,
  isCreating = false,
  onCreateTemplate,
  onTriggerChange,
  onUpdateTemplate,
  onDeleteTemplate,
  canEditTemplates,
  savingTemplate = false,
  onChangesSaved,
  onChangesDetected,
  onTriggerKeyChange
}) => {
  const [formData, setFormData] = useState({
    name: template?.name || '',
    template_key: template?.template_key || '',
    subject: template?.subject || '',
    body_html: template?.body_html || '',
    body_text: template?.body_text || '',
    message_body: template?.message_body || ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [errors, setErrors] = useState({});
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  // Update form when template changes
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        template_key: template.template_key || '',
        subject: template.subject || '',
        body_html: template.body_html || '',
        body_text: template.body_text || '',
        message_body: template.message_body || ''
      });
      setHasChanges(false);
      setErrors({});
    }
  }, [template]);

  // Track changes
  useEffect(() => {
    if (template) {
      const changed =
        formData.name !== (template.name || '') ||
        formData.template_key !== (template.template_key || '') ||
        formData.subject !== (template.subject || '') ||
        formData.body_html !== (template.body_html || '') ||
        formData.body_text !== (template.body_text || '') ||
        formData.message_body !== (template.message_body || '');

      setHasChanges(changed);
      if (changed && onChangesDetected) {
        onChangesDetected();
      }
    }
  }, [formData, template, onChangesDetected]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // If HTML changes, auto-generate plain text
    if (field === 'body_html') {
      setFormData(prev => ({
        ...prev,
        body_text: htmlToText(value)
      }));
    }

    // If trigger key changes, notify parent for header update AND apply defaults
    if (field === 'template_key') {
      if (onTriggerKeyChange) {
        onTriggerKeyChange(value);
      }
      if (onTriggerChange) {
        onTriggerChange(value);  // Apply default content
      }
    }

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Enhanced TinyMCE setup with custom Variables button AND Payment Button
  const setupTinyMCE = (editor) => {
    // Get available variables for this template type
    const availableCategories = VARIABLE_CATEGORIES.filter(category =>
      !category.emailOnly || templateType === 'email'
    );

    // Create menu items for variables
    const createVariableMenuItems = () => {
      return availableCategories.map(category => ({
        type: 'nestedmenuitem',
        text: category.label,
        getSubmenuItems: () => category.variables.map(variable => ({
          type: 'menuitem',
          text: variable.label,
          onAction: () => {
            // Insert as pill instead of brackets
            editor.insertContent(`<span class="variable-pill" data-variable="${variable.key}" contenteditable="false">${variable.label}</span>&nbsp;`);
          }
        }))
      }));
    };

    // Register the Variables button
    editor.ui.registry.addMenuButton('variablesButton', {
      text: '📧 Variables',
      fetch: (callback) => {
        callback(createVariableMenuItems());
      }
    });

    // Register Payment Button for invoice templates
    if (templateType === 'email') {
      editor.ui.registry.addButton('paymentButton', {
        text: '💳 Payment Button',
        tooltip: 'Insert Payment Button',
        onAction: () => {
          const buttonHtml = `
            <div style="text-align: center; margin: 24px 0;">
              <table cellpadding="0" cellspacing="0" style="margin: 0 auto;">
                <tr>
                  <td style="background-color: #10b981; border-radius: 8px; padding: 16px 32px;">
                    <a href="{{payment_url}}" style="color: white; text-decoration: none; font-weight: 600; font-size: 16px; display: block;">
                      Pay Invoice Online
                    </a>
                  </td>
                </tr>
              </table>
              <p style="color: #6b7280; font-size: 14px; margin: 8px 0 0 0;">
                Click to pay securely with credit card
              </p>
            </div>
          `;
          editor.insertContent(buttonHtml);
        }
      });
    }

    // Register search functionality
    editor.ui.registry.addButton('variablesSearch', {
      text: '🔍',
      tooltip: 'Search Variables',
      onAction: () => {
        const searchTerm = prompt('Search for a variable:');
        if (searchTerm) {
          // Find matching variables
          const allVariables = availableCategories.flatMap(cat => cat.variables);
          const matches = allVariables.filter(v =>
            v.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.key.toLowerCase().includes(searchTerm.toLowerCase())
          );

          if (matches.length > 0) {
            const variable = matches[0]; // Use first match
            editor.insertContent(`<span class="variable-pill" data-variable="${variable.key}" contenteditable="false">${variable.label}</span>&nbsp;`);
          } else {
            alert('No variables found matching your search.');
          }
        }
      }
    });
  };

  // Handle creating real template from temporary template
  const handleCreate = async () => {
    try {
      // Validate form
      const newErrors = {};

      if (!formData.name.trim()) {
        newErrors.name = 'Template name is required';
      }

      if (!formData.template_key) {
        newErrors.template_key = 'Trigger event is required';
      }

      if (templateType === 'email') {
        if (!formData.subject.trim()) {
          newErrors.subject = 'Email subject is required';
        }
        if (!formData.body_html.trim()) {
          newErrors.body_html = 'Email content is required';
        }
      } else {
        if (!formData.message_body.trim()) {
          newErrors.message_body = 'SMS message is required';
        }
        if (formData.message_body.length > 160) {
          newErrors.message_body = 'SMS message should be under 160 characters for best delivery';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Prepare creation data
      const createData = {
        name: formData.name.trim(),
        template_key: formData.template_key,
        active: false
      };

      if (templateType === 'email') {
        createData.subject = formData.subject;
        createData.body_html = formData.body_html;
        createData.body_text = htmlToText(formData.body_html);
      } else {
        createData.message_body = formData.message_body;
      }

      await onCreateTemplate(createData);

      setHasChanges(false);
      setErrors({});

      if (onChangesSaved) {
        onChangesSaved();
      }
    } catch (error) {
      console.error('Failed to create template:', error);
      setErrors({ general: 'Failed to create template. Please try again.' });
    }
  };

  const handleSave = async () => {
    try {
      // Validate form
      const newErrors = {};

      if (!formData.template_key) {
        newErrors.template_key = 'Trigger event is required';
      }

      if (templateType === 'email') {
        if (!formData.subject.trim()) {
          newErrors.subject = 'Email subject is required';
        }
        if (!formData.body_html.trim()) {
          newErrors.body_html = 'Email content is required';
        }
      } else {
        if (!formData.message_body.trim()) {
          newErrors.message_body = 'SMS message is required';
        }
        if (formData.message_body.length > 160) {
          newErrors.message_body = 'SMS message should be under 160 characters for best delivery';
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      // Prepare update data
      const updateData = {
        name: formData.name.trim(),
        template_key: formData.template_key,
        active: template.active // Keep existing active status
      };

      if (templateType === 'email') {
        updateData.subject = formData.subject;
        updateData.body_html = formData.body_html;
        // Always auto-generate plain text from HTML
        updateData.body_text = htmlToText(formData.body_html);
      } else {
        updateData.message_body = formData.message_body;
      }

      await onUpdateTemplate(template.id, updateData);

      setHasChanges(false);
      setErrors({});

      if (onChangesSaved) {
        onChangesSaved();
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      setErrors({ general: 'Failed to save changes. Please try again.' });
    }
  };

  const handleDelete = async () => {
    try {
      await onDeleteTemplate(template.id);
      setIsConfirmingDelete(false);
    } catch (error) {
      console.error('Failed to delete template:', error);
      setErrors({ general: 'Failed to delete template. Please try again.' });
      setIsConfirmingDelete(false);
    }
  };

  if (!canEditTemplates) {
    return (
      <Card className="h-full flex items-center justify-center">
        <CardContent className="text-center p-8">
          <Lock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <CardTitle className="mb-2">Edit Restricted</CardTitle>
          <CardDescription>
            You don't have permission to edit templates. Contact an administrator for access.
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  const availableTriggerEvents = getAvailableTriggers(templateType).map(trigger => ({
    value: trigger.key,
    label: trigger.label
  }));

  return (
    <div className="h-full flex flex-col">
      {/* SCROLLABLE CONTENT AREA */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* General Error */}
        {errors.general && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-6">
            <p className="text-red-200">{errors.general}</p>
          </div>
        )}

        <div className="space-y-8">
          {/* Template Configuration */}
          <Card>
            <CardHeader>
              <CardTitle>Template Configuration</CardTitle>
              <CardDescription>
                Set up when and how this template is used
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Trigger Event Selection */}
              <div className="space-y-2">
                <Label htmlFor="trigger">
                  Trigger Event <span className="text-red-400">*</span>
                </Label>
                <Select
                  value={formData.template_key}
                  onValueChange={(value) => handleInputChange('template_key', value)}
                >
                  <SelectTrigger className={cn(
                    errors.template_key && "border-red-500 focus:ring-red-500"
                  )}>
                    <SelectValue placeholder="Select when this template is sent..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTriggerEvents.map(event => (
                      <SelectItem key={event.value} value={event.value}>
                        {event.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.template_key && (
                  <p className="text-red-400 text-sm">{errors.template_key}</p>
                )}
                <p className="text-muted-foreground text-sm">
                  This determines when the template is automatically sent to customers
                </p>
              </div>

              {/* Template Name */}
              <div className="space-y-2">
                <Label htmlFor="name">
                  Template Name <span className="text-red-400">*</span>
                </Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={cn(
                    errors.name && "border-red-500 focus:ring-red-500"
                  )}
                  placeholder="Enter a name for this template"
                />
                {errors.name && (
                  <p className="text-red-400 text-sm">{errors.name}</p>
                )}
                <p className="text-muted-foreground text-sm">
                  This name will appear in the templates list
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Email Template Content */}
          {templateType === 'email' && (
            <Card>
              <CardHeader>
                <CardTitle>Email Content</CardTitle>
                <CardDescription>
                  Design your email template with subject and body content
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="subject">
                    Subject Line <span className="text-red-400">*</span>
                  </Label>
                  <Input
                    id="subject"
                    type="text"
                    value={formData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    className={cn(
                      errors.subject && "border-red-500 focus:ring-red-500"
                    )}
                    placeholder="Enter email subject (use {{customer_name}} for personalization)"
                  />
                  {errors.subject && (
                    <p className="text-red-400 text-sm">{errors.subject}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>
                    Email Content <span className="text-red-400">*</span>
                  </Label>
                  <div className="border border-border rounded-lg overflow-hidden">
                    <Editor
                      apiKey="my59eiego3xd7y85nmnplr3ir3z12ef2wwh2qdxueuu6hcti"
                      value={convertBracketsToPills(formData.body_html)}
                      onEditorChange={(content) => {
                        // Convert pills back to brackets before storing in state
                        const bracketContent = convertPillsToBrackets(content);
                        handleInputChange('body_html', bracketContent);
                      }}
                      init={{
                        height: 400,
                        menubar: false,
                        plugins: [
                          'lists', 'link', 'code', 'table', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link | variablesButton paymentButton variablesSearch | code help',
                        skin: 'oxide-dark',
                        content_css: 'dark',
                        content_style: `
                          body {
                            font-family: system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
                            font-size: 14px;
                            line-height: 1.6;
                            color: #e2e8f0;
                            background: #1e293b;
                            padding: 16px;
                            margin: 0;
                            border-radius: 8px;
                          }

                          h1, h2, h3, h4, h5, h6 {
                            color: #f1f5f9;
                            margin-top: 1.5em;
                            margin-bottom: 0.5em;
                          }

                          h1 { font-size: 1.875rem; font-weight: 700; }
                          h2 { font-size: 1.5rem; font-weight: 600; }
                          h3 { font-size: 1.25rem; font-weight: 600; }

                          p {
                            margin-bottom: 1em;
                            color: #e2e8f0;
                          }

                          strong, b {
                            color: #f1f5f9;
                            font-weight: 600;
                          }

                          a {
                            color: #60a5fa;
                            text-decoration: underline;
                          }

                          a:hover {
                            color: #93c5fd;
                          }

                          ul, ol {
                            margin-bottom: 1em;
                            padding-left: 1.5em;
                          }

                          li {
                            margin-bottom: 0.25em;
                            color: #e2e8f0;
                          }

                          blockquote {
                            border-left: 4px solid #475569;
                            margin: 1.5em 0;
                            padding-left: 1em;
                            color: #cbd5e1;
                            font-style: italic;
                          }

                          code {
                            background: #374151;
                            color: #fbbf24;
                            padding: 0.125em 0.25em;
                            border-radius: 0.25rem;
                            font-family: 'Courier New', monospace;
                          }

                          /* Variable styling */
                          span[data-variable] {
                            background: #1e40af20;
                            color: #60a5fa;
                            padding: 0.125em 0.25em;
                            border-radius: 0.25rem;
                            border: 1px solid #1e40af40;
                            font-family: monospace;
                            font-size: 0.875em;
                          }
                        `,
                        setup: setupTinyMCE
                      }}
                    />
                  </div>
                  {errors.body_html && (
                    <p className="text-red-400 text-sm">{errors.body_html}</p>
                  )}
                </div>
              </CardContent>
              </Card>
            )}

          {/* SMS Template Content */}
          {templateType === 'sms' && (
            <Card>
              <CardHeader>
                <CardTitle>SMS Content</CardTitle>
                <CardDescription>
                  Create your SMS message template
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="message">
                    Message <span className="text-red-400">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    value={formData.message_body}
                    onChange={(e) => handleInputChange('message_body', e.target.value)}
                    rows={4}
                    maxLength={320}
                    className={cn(
                      "resize-none",
                      errors.message_body && "border-red-500 focus:ring-red-500"
                    )}
                    placeholder="Enter SMS message (use {{customer_name}} for personalization)"
                  />
                  <div className="flex justify-between items-center">
                    {errors.message_body && (
                      <p className="text-red-400 text-sm">{errors.message_body}</p>
                    )}
                    <p className={cn(
                      "text-sm ml-auto",
                      formData.message_body.length > 160
                        ? "text-amber-400"
                        : "text-muted-foreground"
                    )}>
                      {formData.message_body.length}/160 characters
                      {formData.message_body.length > 160 && " (will be split into multiple SMS)"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* FIXED FOOTER - Action Buttons */}
      <div className="flex-shrink-0 p-6 border-t border-border bg-muted/30">
        <div className="flex items-center justify-between">
          {!isCreating && (
            !isConfirmingDelete ? (
              <Button
                variant="destructive"
                onClick={() => setIsConfirmingDelete(true)}
                disabled={savingTemplate}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete Template
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={savingTemplate}
                >
                  <Check className="mr-2 h-4 w-4" />
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setIsConfirmingDelete(false)}
                  disabled={savingTemplate}
                >
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
              </div>
            )
          )}

          {isCreating && <div />} {/* Spacer for creation mode */}

          <div className="flex items-center gap-4">
            <div className="text-sm text-muted-foreground">
              {isCreating
                ? (hasChanges ? 'Ready to create template' : 'Fill in the details above')
                : (hasChanges ? 'You have unsaved changes' : 'All changes saved')
              }
            </div>

            <Button
              onClick={isCreating ? handleCreate : handleSave}
              disabled={savingTemplate || (isCreating ? !formData.name.trim() || !formData.template_key : !hasChanges)}
            >
              {savingTemplate ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  {isCreating ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isCreating ? 'Create Template' : 'Save Changes'}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunicationsTemplateDetailsFormShadcn;